/**
 * Local-only image labels — PROTOTYPE HACK.
 *
 * Image blocks have no real display name, so the caption falls back to the
 * literal "Image". Product wants them to read as incrementing, zero-padded
 * numbers ("004") that the user can rename inline.
 *
 * Until the API persists a real per-block display name, BOTH the auto-assigned
 * sequence number and any manual rename live in `localStorage` on this device.
 * Nothing here round-trips to the server — clear site data and it all resets.
 *
 * Replace-me later: move the name onto the block (server-side) and assign the
 * number at ingest time. See `production/alpha/README.md` →
 * "Temporary local-prototyping hacks".
 */
import { useSyncExternalStore } from "react";
/** localStorage key holding the whole `blockId → label` map. */
const STORE_KEY = "vlab:image-labels";
/** Cap a manual rename so a pathological paste can't bloat localStorage. */
const MAX_IMAGE_NAME_LENGTH = 80;
/** Digits to zero-pad the auto number to (4 → "004", 137 → "137"). */
const SEQ_PAD = 3;
// Module-level singleton mirrored to localStorage. Components subscribe via
// `useImageLabelStore`; writes (`ensureImageSeqs`, `renameImage`) replace the
// `store` reference and notify so `useSyncExternalStore` re-renders.
let store = loadStore();
const listeners = new Set();
function loadStore() {
    try {
        const raw = localStorage.getItem(STORE_KEY);
        if (!raw)
            return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object")
            return {};
        const out = {};
        for (const [id, value] of Object.entries(parsed)) {
            if (!value || typeof value !== "object")
                continue;
            const seq = value.seq;
            if (typeof seq !== "number" || Number.isFinite(seq) === false)
                continue;
            const name = value.name;
            out[id] = { seq, name: typeof name === "string" && name ? name : undefined };
        }
        return out;
    }
    catch {
        return {};
    }
}
function persist() {
    try {
        localStorage.setItem(STORE_KEY, JSON.stringify(store));
    }
    catch {
        // storage disabled / quota exceeded — prototype, so silently drop.
    }
}
function emit() {
    for (const listener of listeners)
        listener();
}
function subscribe(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
function getSnapshot() {
    return store;
}
/** Highest sequence currently assigned (0 when the store is empty). */
function highestSeq() {
    let max = 0;
    for (const id in store) {
        const seq = store[id]?.seq ?? 0;
        if (seq > max)
            max = seq;
    }
    return max;
}
/** Zero-padded display form of a sequence number (4 → "004"). */
export function formatImageSeq(seq) {
    return String(seq).padStart(SEQ_PAD, "0");
}
/**
 * Assign a sequence number to every id that doesn't have one yet, in the given
 * order — each taking `(highest assigned so far) + 1`. Callers should pass ids
 * oldest-first so the newest capture earns the highest number.
 *
 * No-ops (and skips the write + notify) when every id is already known, so it's
 * safe to call from a layout effect on every render.
 */
export function ensureImageSeqs(ids) {
    let max = highestSeq();
    let draft;
    for (const id of ids) {
        if (store[id])
            continue;
        if (!draft)
            draft = { ...store };
        max += 1;
        draft[id] = { seq: max };
    }
    if (draft) {
        store = draft;
        persist();
        emit();
    }
}
/**
 * Set (or clear) the manual name for a block. An empty string reverts to the
 * auto number. Assigns a sequence first if the block somehow has none yet.
 */
export function renameImage(blockId, rawName) {
    const name = rawName.trim().slice(0, MAX_IMAGE_NAME_LENGTH);
    const seq = store[blockId]?.seq ?? highestSeq() + 1;
    store = { ...store, [blockId]: { seq, name: name || undefined } };
    persist();
    emit();
}
/** Subscribe a component to the label store (re-renders on assign / rename). */
export function useImageLabelStore() {
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
